import { showFeedback } from "@/components/mm/FeedbackPopup";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getAuthToken, initAuthToken, setAuthToken } from "@/lib/api";

import { nativeOffline, offline } from "@/lib/offlineNative";
import { authFailureAction } from "@/lib/authFailure";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = signed out
  const [authError, setAuthError] = useState("");

  const check = useCallback(async () => {
    if (!getAuthToken()) { setAuthError(""); return setUser(false); }
    try {
      const { data } = await api.get("/auth/me");
      if (nativeOffline) await offline.identify(data, getAuthToken(), offline.state.backgroundAllowed);
      setAuthError(""); setUser(data);
    } catch (e) {
      if (nativeOffline && (!e.response || e.response.status >= 500 || e.response.status === 429) && offline.state.user) {
        const token = getAuthToken();
        try {
          const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
          if (payload.exp * 1000 > Date.now() && payload.sub === offline.state.user.user_id) { setAuthError(""); return setUser(offline.state.user); }
        } catch { /* invalid or expired cached session fails closed */ }
      }
      if (authFailureAction(e.response?.status) === 'signout') {
        await setAuthToken(null, false, true);
        setAuthError(""); setUser(false);
      } else {
        // Preserve the saved token during network, rate-limit and server
        // outages. The user can retry without being forced to sign in again.
        setAuthError("BudgetPulse could not verify your session. Check your connection and retry.");
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    // Skip /me while returning from Google; AuthCallback exchanges the session first
    if (window.location.hash?.includes("session_id=")) return setUser(false);
    initAuthToken().then(check).catch(() => { setAuthError("BudgetPulse could not read your saved session. Retry, or clear app data to sign in again."); setUser(null); });
  }, [check]);

  useEffect(() => {
    const out = () => setUser(false);
    window.addEventListener("mm-logout", out);
    return () => window.removeEventListener("mm-logout", out);
  }, []);

  // Age confirmation and profile/MFA updates use the existing setUser API.
  // Keep their cached identity current without changing those caller semantics.
  useEffect(() => {
    if (nativeOffline && user?.user_id && getAuthToken()) {
      offline.identify(user, getAuthToken(), offline.state.backgroundAllowed).catch(() => {});
    }
  }, [user]);

  const signIn = async (token, u, rememberMe = false) => {
    await setAuthToken(token, rememberMe);
    if (nativeOffline) await offline.identify(u, token, rememberMe);
    setAuthError(""); setUser(u);
    showFeedback("Welcome to BudgetPulse", "You are signed in securely.", { id:"signed-in" });
  };

  const signOut = async () => {
    if (nativeOffline) await offline.clear();
    try { await api.post("/auth/logout"); } catch (e) { /* already signed out */ }
    await setAuthToken(null);
    setUser(false);
  };

  const finishAccountDeletion = async () => {
    await setAuthToken(null);
    setUser(false);
  };

  const retryAuth = async () => {
    setAuthError(""); setUser(null);
    try {
      await initAuthToken();
      await check();
    } catch {
      setAuthError("BudgetPulse could not read your saved session. Retry, or clear app data to sign in again.");
      setUser(null);
    }
  };

  return <AuthCtx.Provider value={{ user, setUser, authError, retryAuth, signIn, signOut, finishAccountDeletion }}>{children}</AuthCtx.Provider>;
}
